import type React from 'react'
import { useMemo } from 'react'
import { memo } from 'react'
import { useEffect, useState } from 'react'

import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  LoadingOverlay,
  Menu,
  Modal,
  Notification,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import {
  IconBackhoe,
  IconDotsVertical,
  IconSearch,
  IconUsers,
} from '@tabler/icons-react'
import { capitalize } from 'lodash-es'

import {
  type SearchResults,
  useAdminUsersSearch,
  type User,
} from '@repo/common/queries/admin/users'

import { TowerCard } from '@/components/ui/TowerCard'
import {
  column,
  mapColumn,
  TowerDataTable,
  type TowerDataTableColumn,
} from '@/components/ui/TowerDataTable'

export type UsersPageProps = {
  query?: string
}

const UsersPage = memo<UsersPageProps>(function UsersPage(
  props: UsersPageProps,
) {
  // const { colorScheme } = useMantineColorScheme()
  // const theme = useMantineTheme()
  // const isDark = colorScheme === 'dark'
  const enableActions = false
  // Search and pagination state
  const [searchInputValue, setSearchInputValue] = useState(props.query || '')
  const [searchQuery, setSearchQuery] = useState(props.query || '')
  const [debouncedSearchQuery] = useDebouncedValue(searchInputValue, 1000)
  const [currentPage, setCurrentPage] = useState(1)

  // Modal state
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [notification, setNotification] = useState<{
    message: string
    color: string
  } | null>(null)

  // Fetch users data
  const { data: usersSearchRaw, isLoading: loading } = useAdminUsersSearch({
    page: currentPage,
    query: searchQuery,
  })

  const results = useMemo<SearchResults | null>(() => {
    return usersSearchRaw || null
  }, [usersSearchRaw])

  const users = useMemo<User[]>(() => {
    return results?.nodes || []
  }, [results])

  // const totalPages = useMemo(() => {
  //   if (!results?.total_count) return 1
  //   const itemsPerPage = 24 // Assuming 24 items per page based on original code
  //   return Math.ceil(results.total_count / itemsPerPage)
  // }, [results])

  useEffect(() => {
    setSearchQuery(debouncedSearchQuery)
    setCurrentPage(1) // Reset to first page on new search
  }, [debouncedSearchQuery])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(event.currentTarget.value)
  }

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      setSearchQuery(searchInputValue)
      setCurrentPage(1)
    }
  }

  const handleResetPassword = (user: User) => {
    setSelectedUser(user)
    setResetPasswordModalOpen(true)
  }

  const confirmResetPassword = () => {
    if (!selectedUser) return

    try {
      // todo await Userfront.api.sendPasswordResetEmail(selectedUser.uuid)

      setNotification({
        message: `Password reset email sent to ${selectedUser.email}`,
        color: 'green',
      })
    } catch (error) {
      console.error('Error sending reset password email:', error)
      setNotification({
        message: 'Failed to send password reset email',
        color: 'red',
      })
    } finally {
      setResetPasswordModalOpen(false)
      setSelectedUser(null)
    }
  }

  const handleEditUser = (_user: User) => {
    // todo navigate(`/users/${_user.uuid}`)
  }

  const getStatus = (user: User) => {
    if (user.locked) {
      return 'locked'
    }
    if (user.roles.length > 0) {
      return 'inactive'
    }
    return 'active'
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green'
      case 'locked':
        return 'red'
      default:
        return 'gray'
    }
  }

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const columns: TowerDataTableColumn<User>[] = [
    mapColumn({
      title: 'User',
      width: '20%',
      render: (_, user: User) => (
        <div style={{ marginBottom: 4 }}>
          <Text fw={600} size="sm" style={{ lineHeight: 1.2 }}>
            {user.email || 'No email'}
          </Text>
          <Text size="sm" c="dimmed">
            {user.name}
          </Text>
        </div>
      ),
    }),
    mapColumn({
      accessor: (user) => getStatus(user),
      title: 'Status',
      width: '10%',
      render: (status) => (
        <Badge color={getStatusColor(status as string)} variant="light">
          {capitalize(status)}
        </Badge>
      ),
    }),
    column({
      accessor: 'roles',
      title: 'Roles',
      width: '15%',
      render: (roles?: string[]) =>
        roles ? <Text size="sm">{roles.join(', ') || 'None'}</Text> : <></>,
    }),
    // column({
    //   accessor: 'wallet_address',
    //   title: 'Wallet Address',
    //   width: '15%',
    //   render: (address) =>
    //     address ? (
    //       <Text size="sm">{(address) || 'Not linked'}</Text>
    //     ) : (
    //       <></>
    //     ),
    // }),
    column({
      accessor: 'active_at',
      title: 'Last Login',
      width: '15%',
      render: (date) =>
        date ? <Text size="sm">{formatDate(date)}</Text> : <></>,
    }),
    // column({
    //   accessor: 'created_at',
    //   title: 'Created',
    //   width: '15%',
    //   render: (date: unknown) =>
    //     date ? <Text size="sm">{formatDate(date as string)}</Text> : <></>,
    // }),
    mapColumn({
      title: 'Actions',
      width: '10%',
      render: (_, user: User) => (
        <Group>
          <Menu position="bottom-end" shadow="md">
            <Menu.Target>
              <ActionIcon>
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() => {
                  handleEditUser(user)
                }}
                disabled={!enableActions}
              >
                Edit User
              </Menu.Item>
              <Menu.Item
                onClick={() => {
                  handleResetPassword(user)
                }}
                disabled={!enableActions}
              >
                Reset Password
              </Menu.Item>
              {/*<Menu.Item disabled={!enableActions}>*/}
              {/*  Send Email*/}
              {/*</Menu.Item>*/}
              {/*<Menu.Divider />*/}
              {/*<Menu.Item color="red" disabled={!enableActions}>*/}
              {/*  Delete User*/}
              {/*</Menu.Item>*/}
            </Menu.Dropdown>
          </Menu>
        </Group>
      ),
    }),
  ]

  const headerContent = (
    <Group>
      <IconSearch size={16} />
      <TextInput
        placeholder="Search users..."
        value={searchInputValue}
        onChange={handleSearchChange}
        onKeyDown={handleSearchKeyDown}
        size="sm"
        style={{ width: 250 }}
      />
    </Group>
  )

  // const footerContent = (
  //   <Group justify="center">
  //     <Pagination
  //       total={totalPages}
  //       value={currentPage}
  //       onChange={setCurrentPage}
  //       withEdges
  //     />
  //   </Group>
  // )

  return (
    <Stack>
      <Alert icon={<IconBackhoe size={30} />} title="Incomplete" color="orange">
        <Text size="sm">This is just the skeleton</Text>
      </Alert>

      {notification && (
        <Notification
          color={notification.color}
          onClose={() => {
            setNotification(null)
          }}
          mb="md"
        >
          {notification.message}
        </Notification>
      )}

      <TowerCard
        title="User Management"
        description="View and manage system users"
        icon={<IconUsers size={20} />}
        extraHeader={headerContent}
      >
        <div style={{ position: 'relative' }}>
          <LoadingOverlay visible={loading} />

          <TowerDataTable
            data={users}
            columns={columns}
            emptyMessage="No users found"
            isLoading={loading}
            onRowClick={handleEditUser}
          />

          {/*{footerContent}*/}
        </div>
      </TowerCard>

      {/* Reset Password Confirmation Modal */}
      <Modal
        opened={resetPasswordModalOpen}
        onClose={() => {
          setResetPasswordModalOpen(false)
        }}
        title="Send Password Reset Email"
        centered
      >
        <Text size="sm" mb="md">
          Are you sure you want to send a password reset email to{' '}
          {selectedUser?.email}?
        </Text>
        <Group>
          <Button
            variant="outline"
            onClick={() => {
              setResetPasswordModalOpen(false)
            }}
          >
            Cancel
          </Button>
          <Button
            color="blue"
            onClick={() => {
              confirmResetPassword()
            }}
          >
            Send Reset Email
          </Button>
        </Group>
      </Modal>
    </Stack>
  )
})

export default UsersPage
